/**
 * API Client for Buffables Backend
 * Handles all HTTP requests with authentication + 401 refresh.
 */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // If 401 and not already retried, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken,
        });

        const { accessToken } = response.data.data;
        localStorage.setItem('accessToken', accessToken);

        // Retry original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - logout user
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// AUTH API
// ============================================

export interface LoginCredentials {
  email: string;
  password: string;
  userType?: 'BUSINESS' | 'CUSTOMER';
}

export interface RegisterBusinessData {
  email: string;
  password: string;
  businessName: string;
  category: string;
  streetAddress: string;
  city: string;
  state?: string;
  country: string;
  zipCode: string;
  phoneNumber: string;
  acceptedTerms: boolean;
}

export interface AuthResponse {
  user: {
    id: string;
    email: string;
    userType: 'BUSINESS' | 'CUSTOMER';
    businessId?: string;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
  };
}

export const authApi = {
  login: (credentials: LoginCredentials) =>
    apiClient.post<{ data: AuthResponse }>('/auth/login', credentials),

  registerBusiness: (data: RegisterBusinessData) =>
    apiClient.post<{ data: AuthResponse }>('/auth/register/business', data),

  logout: () => apiClient.post('/auth/logout'),

  getCurrentUser: () =>
    apiClient.get<{ data: AuthResponse['user'] }>('/auth/me'),

  refreshToken: (refreshToken: string) =>
    apiClient.post<{ data: { accessToken: string } }>('/auth/refresh', { refreshToken }),
};

// ============================================
// BUSINESS API
// ============================================

export interface Business {
  id: string;
  businessName: string;
  category: string;
  address: string;
  phoneNumber: string;
  website?: string;
  orderLink?: string;
  description?: string;
  logo?: string;
  status: 'PENDING_SETUP' | 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';
  walletAddress: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateBusinessData {
  businessName?: string;
  category?: string;
  address?: string;
  phoneNumber?: string;
  website?: string;
  orderLink?: string;
  description?: string;
  logo?: string;
}

export const businessApi = {
  getProfile: () =>
    apiClient.get<{ data: Business }>('/business/profile'),

  updateProfile: (data: UpdateBusinessData) =>
    apiClient.put<{ data: Business }>('/business/profile', data),

  getDashboardMetrics: (params?: { startDate?: string; endDate?: string; range?: string }) =>
    apiClient.get('/business/dashboard', { params }),

  getInsights: (params?: { range?: string }) =>
    apiClient.get('/business/insights', { params }),

  getTiers: () =>
    apiClient.get<{ data: {
      tierDistribution: { BRONZE: number; SILVER: number; GOLD: number; PLATINUM: number; total: number };
      topCustomers: Array<{
        id: string; rank: number; name: string; email: string;
        tier: 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM';
        b3trBalance: number; lifetimeEarned: number;
        totalCheckIns: number; streakDays: number; tierProgress: number;
      }>;
    } }>('/business/tiers'),
};

// ============================================
// REFERRALS API
// ============================================

export interface ReferralConfig {
  id: string;
  businessId: string;
  referrerReward: number;
  refereeReward: number;
  bonusMultiplier: number;
  enabled: boolean;
}

export interface ReferralLeaderboardEntry {
  customerId: string;
  customerName: string;
  email: string;
  totalReferrals: number;
  completedReferrals: number;
  totalRewardsEarned: number;
}

export interface ReferralOverview {
  totalReferrals: number;
  completedReferrals: number;
  pendingReferrals: number;
  totalRewardsIssued: number;
}

export const referralsApi = {
  getConfig: () =>
    apiClient.get<{ data: ReferralConfig }>('/referrals/config'),

  updateConfig: (data: { referrerReward: number; refereeReward: number; bonusMultiplier?: number }) =>
    apiClient.put<{ data: ReferralConfig }>('/referrals/config', data),

  getLeaderboard: (limit = 10) =>
    apiClient.get<{ data: ReferralLeaderboardEntry[] }>('/referrals/leaderboard', { params: { limit } }),

  getOverview: () =>
    apiClient.get<{ data: ReferralOverview }>('/referrals/overview'),
};

// ============================================
// CHALLENGES API
// ============================================

export type ChallengeType =
  | 'SPEND_AMOUNT'
  | 'VISIT_COUNT'
  | 'EARN_TOKENS'
  | 'STREAK'
  | 'TIME_BASED'
  | 'ITEM_SPECIFIC'
  | 'QR_SCAVENGER_HUNT'
  | 'PHOTO_CHALLENGE'
  | 'SOCIAL_SHARE'
  | 'REFERRAL_RACE'
  | 'COMMUNITY_GOAL';

export type ChallengeRewardType =
  | 'POINTS'
  | 'GIFT_CARD'
  | 'DISCOUNT'
  | 'MERCH'
  | 'FREE_ITEM'
  | 'CUSTOM';

export interface Challenge {
  id: string;
  businessId: string;
  name: string;
  description: string;
  type: ChallengeType;
  targetValue: number;
  b3trReward: number;
  rewardType: ChallengeRewardType;
  rewardValue?: number;
  rewardDescription?: string;
  startDate: string;
  endDate: string;
  status: 'ACTIVE' | 'DRAFT' | 'COMPLETED' | 'EXPIRED';
  maxParticipants?: number;
  currentParticipants: number;
  participantCount?: number;
  submissionCount?: number;
  imageUrl?: string;
  metadata?: Record<string, any>;
  isCompetitive: boolean;
  topNWinners?: number;
  communityProgress?: number;
  createdAt: string;
  updatedAt?: string;
}

export interface ChallengeSubmission {
  id: string;
  challengeId: string;
  customerId: string;
  type: string;
  data: Record<string, any>;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reviewedAt?: string;
  createdAt: string;
  challenge?: { name: string; type: string };
  customer?: { firstName: string; lastName: string; phoneNumber: string };
}

export interface CreateChallengeData {
  name: string;
  description: string;
  type: ChallengeType;
  targetValue: number;
  b3trReward: number;
  rewardType?: ChallengeRewardType;
  rewardValue?: number;
  rewardDescription?: string;
  startDate: string;
  endDate: string;
  maxParticipants?: number;
  imageUrl?: string;
  metadata?: Record<string, any>;
  isCompetitive?: boolean;
  topNWinners?: number;
}

export const challengesApi = {
  getAll: (status?: string) =>
    apiClient.get<{ data: Challenge[] }>('/business/challenges', { params: { status } }),

  create: (data: CreateChallengeData) =>
    apiClient.post<{ data: Challenge }>('/business/challenges', data),

  update: (id: string, data: Partial<CreateChallengeData> & { status?: string }) =>
    apiClient.put<{ data: Challenge }>(`/business/challenges/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/business/challenges/${id}`),

  getPendingSubmissions: () =>
    apiClient.get<{ data: ChallengeSubmission[] }>('/business/challenges/submissions/pending'),

  reviewSubmission: (submissionId: string, approved: boolean) =>
    apiClient.post<{ data: ChallengeSubmission }>(`/business/challenges/submissions/${submissionId}/review`, { approved }),
};

// ============================================
// REWARDS CONFIGURATION API
// ============================================

export interface RewardsConfig {
  id: string;
  businessId: string;
  baseRateEnabled: boolean;
  b3trPerDollar: number;
  minPurchase: number;
  redemptionEnabled: boolean;
  minRedemption: number;
  maxRedemption?: number;
  redemptionRate: number;
  bonusActions: BonusAction[];
}

export interface BonusAction {
  id: string;
  name: string;
  type: 'REUSABLE_CUP' | 'FIRST_VISIT' | 'REFERRAL' | 'CUSTOM';
  bonusAmount: number;
  enabled: boolean;
}

export interface UpdateRewardsConfigData {
  baseRateEnabled?: boolean;
  b3trPerDollar?: number;
  minPurchase?: number;
  redemptionEnabled?: boolean;
  minRedemption?: number;
  maxRedemption?: number;
  redemptionRate?: number;
}

export const rewardsApi = {
  getConfig: () =>
    apiClient.get<{ data: RewardsConfig }>('/business/rewards/config'),

  updateConfig: (data: UpdateRewardsConfigData) =>
    apiClient.put<{ data: RewardsConfig }>('/business/rewards/config', data),

  createBonusAction: (data: Omit<BonusAction, 'id'>) =>
    apiClient.post<{ data: BonusAction }>('/business/rewards/bonus-actions', data),

  updateBonusAction: (id: string, data: Partial<BonusAction>) =>
    apiClient.put<{ data: BonusAction }>(`/business/rewards/bonus-actions/${id}`, data),

  deleteBonusAction: (id: string) =>
    apiClient.delete(`/business/rewards/bonus-actions/${id}`),
};

// ============================================
// REWARDS CATALOG API
// ============================================

export enum RewardCategory {
  FOOD = 'FOOD',
  BEVERAGE = 'BEVERAGE',
  DISCOUNT = 'DISCOUNT',
  MERCHANDISE = 'MERCHANDISE',
  EXPERIENCE = 'EXPERIENCE',
  CUSTOM = 'CUSTOM',
}

export enum RewardStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  OUT_OF_STOCK = 'OUT_OF_STOCK',
  ARCHIVED = 'ARCHIVED',
}

export type CustomerTier = 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM';

export interface Reward {
  id: string;
  businessId: string;
  name: string;
  description?: string;
  category: RewardCategory;
  imageUrl?: string;
  pointsCost: number;
  stockQuantity?: number;
  maxPerCustomer?: number;
  minTier: CustomerTier;
  status: RewardStatus;
  startDate?: string;
  endDate?: string;
  totalRedemptions: number;
  createdAt: string;
  updatedAt: string;
}

export type RewardType =
  | 'POINTS_DISCOUNT'
  | 'PERCENTAGE_DISCOUNT'
  | 'FREE_ITEM'
  | 'BOGO'
  | 'ITEM_DISCOUNT'
  | 'TIME_BASED';

export type DiscountType = 'DOLLAR' | 'PERCENTAGE';

export interface BogoConfig {
  buyQuantity: number;
  getQuantity: number;
  freeItemValue?: number;
}

export interface TimeRestriction {
  startHour: number;
  endHour: number;
  daysOfWeek: number[];
  discountType: DiscountType;
  discountValue: number;
}

export interface CreateRewardData {
  name: string;
  description?: string;
  category?: RewardCategory;
  imageUrl?: string;
  pointsCost: number;
  stockQuantity?: number;
  maxPerCustomer?: number;
  minTier?: CustomerTier;
  startDate?: string;
  endDate?: string;
  rewardType?: RewardType;
  discountType?: DiscountType;
  discountValue?: number;
  maxDiscountCap?: number;
  bogoConfig?: BogoConfig;
  timeRestriction?: TimeRestriction;
  maxUsesPerDay?: number;
  linkedItemId?: string;
}

export interface UpdateRewardData {
  name?: string;
  description?: string;
  category?: RewardCategory;
  imageUrl?: string;
  pointsCost?: number;
  stockQuantity?: number;
  maxPerCustomer?: number;
  minTier?: CustomerTier;
  status?: RewardStatus;
  startDate?: string;
  endDate?: string;
  rewardType?: RewardType;
  discountType?: DiscountType;
  discountValue?: number;
  maxDiscountCap?: number;
  bogoConfig?: BogoConfig;
  timeRestriction?: TimeRestriction;
  maxUsesPerDay?: number;
  linkedItemId?: string;
}

export interface RewardFilters {
  status?: RewardStatus;
  category?: RewardCategory;
  includeInactive?: boolean;
}

export const rewardCatalogApi = {
  getAll: (filters?: RewardFilters) =>
    apiClient.get<{ data: Reward[]; meta: { count: number; timestamp: string } }>(
      '/business/rewards',
      { params: filters }
    ),

  getById: (id: string) =>
    apiClient.get<{ data: Reward }>(`/business/rewards/${id}`),

  create: (data: CreateRewardData) =>
    apiClient.post<{ data: Reward; meta: { message: string; timestamp: string } }>(
      '/business/rewards',
      data
    ),

  update: (id: string, data: UpdateRewardData) =>
    apiClient.put<{ data: Reward; meta: { message: string; timestamp: string } }>(
      `/business/rewards/${id}`,
      data
    ),

  delete: (id: string) =>
    apiClient.delete<{ meta: { message: string; timestamp: string } }>(`/business/rewards/${id}`),

  updateStock: (id: string, quantity: number) =>
    apiClient.patch<{ data: Reward; meta: { message: string; timestamp: string } }>(
      `/business/rewards/${id}/stock`,
      { quantity }
    ),
};

// ============================================
// LOCATIONS API
// ============================================

export interface Location {
  id: string;
  businessId: string;
  name: string;
  address: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phoneNumber?: string;
  latitude?: number;
  longitude?: number;
  qrCode: string;
  qrCodeUrl?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLocationData {
  name: string;
  address: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phoneNumber?: string;
  latitude?: number;
  longitude?: number;
}

export const locationsApi = {
  getAll: () =>
    apiClient.get<{ data: Location[] }>('/business/locations'),

  getById: (id: string) =>
    apiClient.get<{ data: Location }>(`/business/locations/${id}`),

  create: (data: CreateLocationData) =>
    apiClient.post<{ data: Location }>('/business/locations', data),

  update: (id: string, data: Partial<CreateLocationData>) =>
    apiClient.put<{ data: Location }>(`/business/locations/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/business/locations/${id}`),

  toggleActive: (id: string) =>
    apiClient.patch(`/business/locations/${id}/toggle-active`),

  getQRCode: (id: string) =>
    apiClient.get<{ data: { qrCode: string; qrCodeUrl: string } }>(`/business/locations/${id}/qr-code`),
};

// ============================================
// CUSTOMERS API
// ============================================

export interface Customer {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  walletAddress: string;
  b3trBalance: number;
  lifetimeEarned: number;
  lifetimeSpent: number;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerFilters {
  search?: string;
  sortBy?: 'createdAt' | 'lifetimeEarned' | 'b3trBalance';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export const customersApi = {
  getAll: (filters?: CustomerFilters) =>
    apiClient.get<{ data: Customer[]; meta: { total: number; page: number; limit: number } }>(
      '/business/customers',
      { params: filters }
    ),

  getById: (id: string) =>
    apiClient.get<{ data: Customer }>(`/business/customers/${id}`),

  getTransactions: (customerId: string, page = 1, limit = 20) =>
    apiClient.get(`/business/customers/${customerId}/transactions`, {
      params: { page, limit },
    }),
};

// ============================================
// TRANSACTIONS API
// ============================================

export interface Transaction {
  id: string;
  customerId: string;
  businessId: string;
  locationId?: string;
  type: 'EARN' | 'REDEEM' | 'BONUS' | 'ADJUSTMENT';
  status: 'PENDING' | 'CONFIRMED' | 'FAILED';
  b3trAmount: number;
  fiatAmount?: number;
  baseAmount?: number;
  bonusAmount?: number;
  multiplier?: number;
  txHash?: string;
  notes?: string;
  createdAt: string;
  confirmedAt?: string;
}

export const transactionsApi = {
  getAll: (filters?: { page?: number; limit?: number; status?: string; type?: string }) =>
    apiClient.get<{ data: Transaction[]; meta: { total: number } }>(
      '/business/transactions',
      { params: filters }
    ),

  getById: (id: string) =>
    apiClient.get<{ data: Transaction }>(`/business/transactions/${id}`),

  export: (filters?: { startDate?: string; endDate?: string; type?: string }) =>
    apiClient.get('/business/transactions/export', {
      params: filters,
      responseType: 'blob',
    }),
};

// ============================================
// SQUARE INTEGRATION API
// ============================================

export interface SquareIntegration {
  connected: boolean;
  merchantId?: string;
  status?: string;
  lastSync?: string;
  expiresAt?: string;
  isExpired?: boolean;
  locations?: Array<{
    id: string;
    name: string;
    address: string;
  }>;
}

export interface LoyaltySyncStatus {
  enabled: boolean;
  lastSyncTime?: string;
  linkedAccountsCount: number;
  syncErrors: string[];
  programName?: string;
}

export interface LoyaltySyncHealth {
  last24h: {
    totalSyncs: number;
    failedSyncs: number;
    successRate: number;
  };
  activeRedemptions: number;
  recentLogs: Array<{
    id: string;
    timestamp: string;
    action: string;
    status: 'SUCCESS' | 'FAILED';
    details?: string;
    customerId?: string;
  }>;
}

export interface SquareCatalogItem {
  id: string;
  name: string;
  description?: string;
  price?: number;
  category?: string;
}

export const squareApi = {
  getStatus: () =>
    apiClient.get<{ data: SquareIntegration }>('/business/square/status'),

  connect: () =>
    apiClient.post<{ data: { authUrl: string } }>('/business/square/connect'),

  disconnect: () =>
    apiClient.post('/business/square/disconnect'),

  syncLocations: () =>
    apiClient.post<{ data: { locations: Array<{ id: string; name: string; address: string }> } }>('/business/square/sync-locations'),

  // Square Loyalty
  enableLoyaltySync: () =>
    apiClient.post<{ data: LoyaltySyncStatus }>('/business/square/loyalty/enable'),

  getLoyaltySyncStatus: () =>
    apiClient.get<{ data: LoyaltySyncStatus }>('/business/square/loyalty/status'),

  syncLoyaltyCustomers: () =>
    apiClient.post<{ data: { syncedCount: number; errors: string[] } }>('/business/square/loyalty/sync'),

  getLoyaltySyncHealth: () =>
    apiClient.get<{ data: LoyaltySyncHealth }>('/business/square/loyalty/health'),

  getSquareCatalogItems: () =>
    apiClient.get<{ data: SquareCatalogItem[] }>('/business/square/catalog/items'),
};

// ============================================
// MARKETING / PUSH NOTIFICATIONS API
// ============================================

export interface PushSendPayload {
  customerId: string;
  title: string;
  body: string;
  data?: Record<string, any>;
  sound?: string;
  badge?: number;
  channelId?: string;
  priority?: 'default' | 'normal' | 'high';
}

export type NotificationTemplateType =
  | 'POINTS_EARNED'
  | 'TIER_UPGRADED'
  | 'CHALLENGE_AVAILABLE'
  | 'REWARD_UNLOCKED'
  | 'BIRTHDAY_REWARD'
  | 'STREAK_REMINDER'
  | 'WIN_BACK'
  | 'TRANSACTION_CONFIRMED';

export interface PushTemplatePayload {
  customerId: string;
  template: {
    type: NotificationTemplateType;
    data: Record<string, any>;
  };
}

export interface ScheduleNotificationPayload {
  customerId: string;
  title: string;
  body: string;
  data?: Record<string, any>;
  sendAt: string; // ISO date string
}

export interface SegmentTemplate {
  name: string;
  description?: string;
  logic?: 'AND' | 'OR';
  conditions: Array<{
    field: string;
    operator: string;
    value: any;
  }>;
}

export interface Segment {
  id: string;
  name: string;
  description: string | null;
  customerCount: number;
  createdAt: string;
  updatedAt: string;
}

export const marketingApi = {
  // --- Push Notifications ---

  /** Send a direct push notification to a customer */
  sendPush: (payload: PushSendPayload) =>
    apiClient.post<{ success: boolean; ticketCount: number }>('/push/send', payload),

  /** Send a templated push notification */
  sendTemplate: (payload: PushTemplatePayload) =>
    apiClient.post<{ success: boolean; ticketCount: number }>('/push/send-template', payload),

  /** Schedule a push notification for future delivery */
  schedulePush: (payload: ScheduleNotificationPayload) =>
    apiClient.post<{ success: boolean; scheduledFor: string }>('/push/schedule', payload),

  // --- Customer Segments ---

  /** Get all saved segments for the business */
  getSegments: () =>
    apiClient.get<{ data: Segment[]; meta: { count: number } }>('/segments'),

  /** Get pre-defined segment templates */
  getSegmentTemplates: () =>
    apiClient.get<{ data: Record<string, SegmentTemplate>; meta: { count: number } }>('/segments/templates'),

  /** Preview customers matching criteria (without saving) */
  previewSegment: (criteria: SegmentTemplate, limit = 100) =>
    apiClient.post('/segments/preview', criteria, { params: { limit } }),

  /** Get customers in a saved segment */
  getSegmentCustomers: (segmentId: string, limit = 500) =>
    apiClient.get(`/segments/${segmentId}/customers`, { params: { limit } }),
};

// ============================================
// ERROR HANDLING
// ============================================

export interface ApiError {
  code: string;
  message: string;
  details?: any;
}

export const handleApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ error: ApiError }>;
    if (axiosError.response?.data?.error) {
      return axiosError.response.data.error;
    }
    return {
      code: 'NETWORK_ERROR',
      message: axiosError.message || 'Network error occurred',
    };
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred',
  };
};

export default apiClient;
